import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

/** How long the anchor stays pinned once it has first come into view. */
const SETTLE_MS = 1500;
/** The reader taking over: the first of these ends the pinning. */
const YIELD_EVENTS = ["wheel", "touchstart", "keydown"] as const;

/**
 * Scrolls the element carrying `anchorId` into view while the URL's hash names
 * it — the deep links the email campaigns point at.
 *
 * The browser scrolls to a hash once, against the DOM as it stands. A dashboard
 * page's data (plans, pricing, banners) lands afterwards and shifts the layout,
 * pushing the anchor past the viewport, so it is re-scrolled on every layout
 * change until things settle. The first scroll, tap or key the reader makes
 * ends that — from then on the scroll position is theirs.
 *
 * Call it from the component that renders the anchor, so the element is in the
 * DOM by the time the effect runs.
 */
export default function useAnchorScroll(anchorId: string) {
  const hash = useRouterState({ select: (state) => state.location.hash });

  useEffect(() => {
    if (hash !== anchorId) return;

    let settleTimer: ReturnType<typeof setTimeout> | undefined;

    function stop() {
      observer.disconnect();
      clearTimeout(settleTimer);
      YIELD_EVENTS.forEach((event) => window.removeEventListener(event, stop));
    }

    function keepInView() {
      const element = document.getElementById(anchorId);
      if (!element) return;
      element.scrollIntoView({ block: "start" });
      // Measured from the first sighting, not restarted by each shift.
      settleTimer ??= setTimeout(stop, SETTLE_MS);
    }

    const observer = new ResizeObserver(keepInView);
    observer.observe(document.body);
    YIELD_EVENTS.forEach((event) =>
      window.addEventListener(event, stop, { passive: true, once: true })
    );

    keepInView();

    return stop;
  }, [anchorId, hash]);
}
